import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo } from "../components/Seo";
import { CandidateCard } from "../components/CandidateCard";
import { MunicipalityMark } from "../components/MunicipalityMark";
import { Reveal } from "../components/Reveal";
import {
    getMunicipality,
    getDetailedCandidatesByMunicipality,
    identifiedCoverage,
    ELECTION_DATE,
} from "../data/site";
import NotFound from "./NotFound";

export default function Municipality({ slug }) {
    const m = getMunicipality(slug);

    useSeo({
        title: m
            ? `${m.name} Mayoral Candidates 2026 — HALTONS.CA`
            : "HALTONS.CA",
        description: m
            ? `Meet the candidates for Mayor of ${m.name} in the 2026 municipal election on ${ELECTION_DATE}. Profiles, backgrounds and stated priorities on HALTONS.CA.`
            : undefined,
        path: m ? `/${m.slug}` : "/",
    });

    if (!m) return <NotFound />;

    const detailed = getDetailedCandidatesByMunicipality(m.slug);
    const identified = identifiedCoverage[m.slug] || [];

    return (
        <main data-testid={`municipality-page-${m.slug}`}>
            <section className="border-b border-border bg-navy text-ivory">
                <div className="fade-up mx-auto flex max-w-6xl flex-col gap-6 px-4 py-16 sm:px-6 sm:py-20 md:flex-row md:items-center md:justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">
                            {m.designation} — 2026 Mayoral Election
                        </p>
                        <h1 className="mt-4 font-serif-display text-4xl font-bold sm:text-5xl">
                            {m.name}
                        </h1>
                        <p className="mt-4 max-w-xl text-sm leading-relaxed text-ivory/75 md:text-base">
                            Candidates contesting the office of Mayor of {m.name} in the municipal
                            election on {ELECTION_DATE}.
                        </p>
                    </div>
                    <MunicipalityMark mark={m.mark} className="h-24 w-24 shrink-0 text-gold" />
                </div>
            </section>

            {detailed.length > 0 && (
                <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6" aria-labelledby="candidates-heading">
                    <Reveal>
                        <h2 id="candidates-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                            Mayoral candidates
                        </h2>
                        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
                            Candidate summaries are brief introductions to backgrounds and stated
                            priorities — they are not endorsements or rankings.
                        </p>
                    </Reveal>
                    <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {detailed.map((c, i) => (
                            <Reveal key={c.slug} delay={i * 0.06} className="h-full">
                                <CandidateCard candidate={c} />
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            {detailed.length > 0 && (
                <section className="mx-auto max-w-6xl px-4 pb-16 sm:px-6" aria-labelledby="compare-heading" data-testid="compare-section">
                    <Reveal>
                        <h2 id="compare-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                            Compare stated priorities
                        </h2>
                        <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
                            Priorities are the candidates' stated campaign commitments, not completed
                            achievements.
                        </p>
                    </Reveal>
                    <Reveal>
                        <div className="mt-6 overflow-x-auto border border-border bg-white">
                            <table className="w-full min-w-[560px] text-left text-sm">
                                <thead>
                                    <tr className="border-b border-border bg-ivory">
                                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-widest text-teal">Candidate</th>
                                        <th className="px-5 py-3 text-xs font-semibold uppercase tracking-widest text-teal">Stated priorities</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailed.map((c) => (
                                        <tr key={c.slug} className="border-b border-border last:border-0">
                                            <td className="whitespace-nowrap px-5 py-4 align-top font-semibold text-navy">
                                                <Link to={`/candidates/${c.slug}`} data-testid={`compare-${c.slug}`} className="link-underline">
                                                    {c.name}
                                                </Link>
                                            </td>
                                            <td className="px-5 py-4 text-muted-foreground">
                                                {c.priorities
                                                    ? c.priorities.items.map((i) => i.title).join(" · ")
                                                    : <span className="italic">Not stated in supplied material</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Reveal>
                </section>
            )}

            {m.slug !== "milton" && (
                <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6" aria-labelledby="coverage-heading">
                    <Reveal>
                        <h2 id="coverage-heading" className="font-serif-display text-2xl font-semibold text-navy sm:text-3xl">
                            Candidate directory
                        </h2>
                        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
                            {detailed.length > 0
                                ? `Additional ${m.name} candidate profiles will be published here as information is supplied — profiles are built only from supplied, sourced material.`
                                : `Detailed candidate information for ${m.name} has not yet been supplied to HALTONS.CA. This directory will be published as candidate information becomes available — profiles are built only from supplied, sourced material.`}
                        </p>
                    </Reveal>
                    {identified.length > 0 && (
                        <div className="mt-8 border border-border bg-white p-6">
                            <p className="text-xs font-semibold uppercase tracking-widest text-teal">
                                Identified for editorial coverage
                            </p>
                            <ul className="mt-3 space-y-2">
                                {identified.map((name) => (
                                    <li key={name} className="font-serif-display text-lg font-semibold text-navy" data-testid={`identified-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                        {name}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-3 text-xs text-muted-foreground">
                                Previously identified for potential coverage in our Emerging Leaders of
                                Halton series. Coverage will follow supplied information.
                            </p>
                        </div>
                    )}
                    <a
                        href={m.officialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`official-link-${m.slug}`}
                        className="mt-8 inline-flex items-center gap-2 border border-navy px-6 py-3 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-ivory"
                    >
                        {m.officialLabel}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </a>
                </section>
            )}

            {m.slug === "milton" && (
                <section className="border-t border-border bg-white">
                    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
                        <a
                            href={m.officialUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="milton-official-candidate-list-link"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-teal link-underline"
                        >
                            Official Town of Milton candidate list
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                        </a>
                    </div>
                </section>
            )}

            <section className="border-t border-border bg-ivory">
                <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
                    <p className="text-xs text-muted-foreground">
                        HALTONS.CA is an independent information website and is not an official
                        municipal election service. Found an error on this page?{" "}
                        <Link to="/corrections" data-testid={`corrections-link-${m.slug}`} className="font-semibold text-teal link-underline">
                            Report a correction
                        </Link>
                        .
                    </p>
                </div>
            </section>
        </main>
    );
}
